/**
 * Wspólny interfejs celu dla petów — używany przez `Pet` (FSM) i `TargetingSystem`.
 *
 * Implementują go:
 *  - `Destructible` (świat / placa) — autorytatywne lokalne HP,
 *  - `ArenaTarget` (World Boss + posągi-pomocnicy w `AtlantisSanctumScene`) —
 *    NIEAUTORYTATYWNE; HP/tarcza liczone serwerowo przez pętlę `/boss/hit`.
 *
 * Dzięki temu pety reużywają tej samej maszynerii biegania/atakowania/efektów
 * niezależnie od tego, czy biją destruktybl, czy kolosa — bez rozgałęziania AI.
 */
export interface PetTarget {
  /** Candy encounters use hit animation without damage numbers. Default true. */
  readonly showDamageNumbers?: boolean;
  readonly x: number;
  readonly y: number;
  /** uidy petów aktualnie namierzających ten cel (anti-overcrowd). */
  readonly attackers: Set<string>;
  /** Czy pet może go TERAZ namierzyć (żywy / nieosłonięty tarczą). */
  isTargetable(): boolean;
  /** Czy są wolne sloty na kolejnego peta (targetable + maxSlots). */
  hasFreeSlot(): boolean;
  /** Zadaj obrażenia. Zwraca true, jeśli cel „zginął" w tym wywołaniu. */
  takeDamage(amount: number): boolean;
}

/**
 * Lekki cel arenowy NIEAUTORYTATYWNY: pety biją go dla EFEKTU (lunge/iskry/popupy
 * pochodzą z własnego animatora peta), ale HP i tarcza są liczone wyłącznie
 * serwerowo (`/boss/hit`). `takeDamage` NIE odejmuje HP (zawsze zwraca false),
 * tylko odpala opcjonalny błysk. Pozycja śledzi sprite przez funkcję `pos()`.
 *
 * Używany dla kolosa (`maxSlots` = cała drużyna) i ożywionych posągów-pomocników
 * (po kilka slotów). Sterowany przez scenę: `setTargetable()` włącza/wyłącza
 * namierzanie (boss osłonięty tarczą = nietykalny → pety przełączają się na posągi).
 */
export class ArenaTarget implements PetTarget {
  readonly attackers = new Set<string>();
  private targetable = false;

  constructor(
    private readonly pos: () => { x: number; y: number },
    private readonly maxSlots: number,
    private readonly onHit?: () => void,
    readonly showDamageNumbers = true,
  ) {}

  get x(): number {
    return this.pos().x;
  }
  get y(): number {
    return this.pos().y;
  }

  setTargetable(v: boolean): void {
    this.targetable = v;
    // Wyłączenie = boss/posąg nietykalny → pety puszczą go natychmiast (FSM peta
    // co klatkę sprawdza isTargetable()); czyścimy sloty, by nie blokowały innych.
    if (!v) this.attackers.clear();
  }

  isTargetable(): boolean {
    return this.targetable;
  }

  hasFreeSlot(): boolean {
    return this.targetable && this.attackers.size < this.maxSlots;
  }

  takeDamage(_amount: number): boolean {
    this.onHit?.();
    return false; // serwer jest autorytetem — lokalne bicie nie zabija celu
  }
}
